import React, { useEffect, useState } from 'react';
//import FullCalendar, { EventClickArg } from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import { useEventContext, EventModel, constructCalendarUrlWithQuery, formatDateString } from '../contexts/EventContext';
import { CalendarApi, EventClickArg } from '@fullcalendar/core';
import FullCalendar from '@fullcalendar/react';
import listPlugin from '@fullcalendar/list';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

//import { TimeColsView } from '@fullcalendar/timegrid/internal.js';

import './Calendar.css';
import { useSwipeable } from 'react-swipeable';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined';
import ViewDayOutlinedIcon from '@mui/icons-material/ViewDayOutlined';
import { ApiBannerMessage, useHttpClientContext } from '../contexts/HttpClientContext';
import BannerMessage from '../components/BannerMessage';
import { useLocation, useNavigate } from 'react-router-dom';
import TodayIcon from '@mui/icons-material/Today';
import { useTranslation } from 'react-i18next';

interface CalendarEvent {
    id: string;
    title: string;
    start: string;
    end: string;
    color: string;
    description: string;
    eventData: EventModel;
}


const useQuery = () => {
    return new URLSearchParams(useLocation().search);
}

const Calendar = () => {
    //const eventsContext = useContext<EventContextType | undefined>(EventContext);
    const eventsContext = useEventContext();
    const httpClient = useHttpClientContext();
    const query = useQuery();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
    const [bannerMessages, setBannerMessages] = useState<ApiBannerMessage[]>([]);

    //const calendarRef = useRef<FullCalendar | null>(null);

    const fetchBannerMessages = () => {
        return httpClient.anonymousHttpClient.get<ApiBannerMessage[]>("banner-messages");
    };

    const handleCalendarNavigation = (direction: "next" | "prev" | "today") => {
        const calendarApi: CalendarApi | undefined = eventsContext.calendarRef.current?.getApi();
        if (calendarApi) {

            switch (direction) {
                case 'next':
                    calendarApi.next();
                    break;
                case 'prev':
                    calendarApi.prev();
                    break;
                case 'today':
                    calendarApi.today();
                    break;
            }

            eventsContext.changeCalendarDate(calendarApi.getDate());
            navigate(constructCalendarUrlWithQuery(eventsContext.calendarView, eventsContext.displayAllEvents, calendarApi.getDate()));
        }
    }

    const handlers = useSwipeable({
        onSwipedLeft: () => handleCalendarNavigation('next'),
        onSwipedRight: () => handleCalendarNavigation('prev'),
        trackTouch: true,
        preventScrollOnSwipe: false

    });

    const eventColor = (event: EventModel) => {
        const colorOrange = '#F95738';
        const colorGreen = '#4DAA57';
        const colorBlue = '#2F6690';
        const colorPurple = '#8E1CA3';
        const colorBrown = '#CA8D2F';

        var color = colorBrown;

        if (!event.preBookingReq) {
            color = colorBlue;
        }
        const booking = event.booking;

        if (booking) {
            if (booking.partnerBooked) {
                color = colorOrange;
            }
            if (booking.booked) {
                color = colorGreen;
            }

        }

        if (event.isPersonal) {
            color = colorPurple;
        }

        return color;
    }

    useEffect(() => {

        eventsContext.events.match({
            success: (data) => {
                const result: CalendarEvent[] = data.map(event => {
                    return {
                        id: event.eventId,
                        title: event.heading,
                        start: event.startDate + 'T' + event.startTime.replace('.', ':').trim(),
                        end: event.startDate + 'T' + event.endTime.replace('.', ':').trim(),
                        color: eventColor(event),
                        description: event.description,
                        eventData: event
                    };
                });

                setCalendarEvents(result);
            }
        });

        const view = query.get("view");
        eventsContext.changeCalendarView(view === "timeGridDay" ? "timeGridDay" : "listWeek");

        const displayAll = query.get("displayAll");
        if (displayAll !== null) {
            eventsContext.toggleDisplayAllEvents(displayAll === "true");
        }

        const date = query.get("date");
        eventsContext.changeCalendarDate(new Date(date || formatDateString(new Date())));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventsContext.events]);

    useEffect(() => {
        fetchBannerMessages().then(response => setBannerMessages(response.data));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    const handleDateClick = (arg: EventClickArg) => {
        console.log(arg);
        eventsContext.openEventModal(arg.event.id);
    };

    return eventsContext.events.match({
        loading: () => (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="80vh"
            >
                <CircularProgress size="6rem" />
                <Typography variant="h6" style={{ marginTop: '16px' }}>
                    {t("calendar.loading")}
                </Typography>
            </Box>
        ),
        success: (data) => {
            return (
                <div {...handlers} style={{
                    touchAction: "pan-y", // allow scrolling AND swiping ..
                    overscrollBehavior: "contain", //TODO: fix the swipe violation warning ..
                    padding: "12px"
                }}>
                    {bannerMessages && bannerMessages.sort((a, b) => a.priority - b.priority).map((message, i) =>
                        <Box mb={1} key={`message-${i}`}>
                            <BannerMessage bannerMessage={message} />
                        </Box>
                    )}
                    <FullCalendar
                        ref={eventsContext.calendarRef}
                        plugins={[dayGridPlugin, listPlugin, timeGridPlugin, interactionPlugin]}
                        initialView={query.get("view") === "timeGridDay" ? query.get("view")! : "listWeek"} //listWeek timeGridDay
                        events={calendarEvents.filter((evt) => {
                            if (evt.eventData.isPersonal && !eventsContext.showPersonalEvents && !evt.eventData.booking) {
                                return false;
                            }
                            return !evt.eventData.preBookingReq
                                || evt.eventData.booking
                                || (eventsContext.displayAllEvents && eventsContext.isEventMemberOf(evt.eventData));
                        })}
                        eventClick={handleDateClick}
                        initialDate={eventsContext.calendarDate}
                        allDaySlot={false}
                        firstDay={new Date().getDay()}
                        headerToolbar={{
                            left: "title",
                            right: ""
                        }}
                        //buttonText={{ today: "Idag" }}
                        dayHeaders={false}
                        views={{
                            "timeGridDay": {
                                displayEventTime: false,
                                titleFormat: {
                                    weekday: 'long',
                                    month: 'short',  //long/short
                                    day: 'numeric',
                                    omitCommas: true,
                                    year: 'numeric'
                                }
                            },
                            "listWeek": {
                                displayEventTime: true,
                                titleFormat: {
                                    month: 'short',
                                    day: 'numeric',
                                    omitCommas: true,
                                    year: 'numeric'
                                }
                            }
                        }}
                        noEventsContent={t("calendar.no_events")}
                        slotMinTime={'06:00:00'}
                        slotMaxTime={'23:00:00'}
                        slotDuration={'00:30:00'}
                        locale={i18n.language}
                        slotEventOverlap={true}
                        nowIndicator={true}
                        contentHeight={"auto"}
                    />
                    <Box
                        sx={{
                            position: 'fixed',
                            bottom: 16,
                            right: 16,
                            zIndex: 1200, // navigation drawer is apparently 1200 ..
                        }}
                    >
                        <SpeedDial
                            ariaLabel="SpeedDial basic example"
                            sx={{ position: 'absolute', bottom: 16, right: 16 }}
                            direction='left'
                            icon={<SpeedDialIcon />}
                        >
                            <SpeedDialAction
                                onClick={() => handleCalendarNavigation("next")}
                                key={"next"}
                                icon={<ArrowForwardIcon />}
                                tooltipTitle={"Nästa"}
                            />
                            <SpeedDialAction
                                onClick={() => handleCalendarNavigation("prev")}
                                color='primary'
                                key={"prev"}
                                icon={<ArrowBackIcon />}
                                tooltipTitle={"Föregående"}
                            />

                            <SpeedDialAction
                                onClick={() => {
                                    const nextView = eventsContext.calendarView === 'listWeek'
                                        ? 'timeGridDay'
                                        : 'listWeek';

                                    eventsContext.changeCalendarView(nextView);
                                    navigate(constructCalendarUrlWithQuery(nextView, eventsContext.displayAllEvents, eventsContext.calendarDate));
                                }}
                                key={"view"}
                                icon={
                                    eventsContext.calendarView === 'listWeek'
                                        ? <ViewDayOutlinedIcon />
                                        : <FormatListBulletedOutlinedIcon />
                                }
                                tooltipTitle={"Ändra vy"}
                            />

                            <SpeedDialAction
                                onClick={() => handleCalendarNavigation("today")}
                                key={"refresh"}
                                icon={<TodayIcon />}
                                tooltipTitle={"Idag"}
                            />
                        </SpeedDial>
                    </Box>

                </div >
            );
        }
    });
};


export default Calendar;